import { supabase, formatSupabaseError } from '../../supabase';
import { cachedQuery, invalidateCache } from '../cache/queryCache';

function parseFeatures(features) {
  if (!features) return [];
  if (Array.isArray(features)) return features;
  return String(features)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function formatDate(value) {
  if (!value) return '—';
  const str = String(value);
  return str.includes('T') ? str.slice(0, 10) : str;
}

function mapSubscription(sub, plan) {
  if (!sub) {
    return {
      planName: '—',
      billingCycle: '—',
      price: 0,
      startDate: '—',
      expiryDate: '—',
      status: 'inactive',
      paymentMethod: '—',
      lastPayment: '—',
      autoRenew: false,
      features: [],
    };
  }

  const subStatus = sub.status === 'active' || sub.status === 'trial' ? 'active' : 'expired';

  return {
    planName: sub.plan_name || plan?.name || '—',
    billingCycle: sub.billing_cycle || '—',
    price: Number(sub.price) || 0,
    startDate: formatDate(sub.start_date),
    expiryDate: formatDate(sub.expiry_date),
    status: subStatus,
    paymentMethod: sub.payment_method || '—',
    lastPayment: formatDate(sub.last_payment_date),
    autoRenew: Boolean(sub.auto_renew),
    features: parseFeatures(plan?.features),
  };
}

function mapAdminRow(admin, gym, subscription, plan) {
  const status = admin.status === 'suspended' || admin.status === 'inactive'
    ? 'suspended'
    : 'active';

  return {
    id: admin.id,
    authUserId: admin.auth_user_id,
    userAccountId: admin.user_account_id,
    gymId: admin.gym_id,
    name: admin.full_name || '—',
    email: admin.email || '—',
    phone: admin.phone || '—',
    gym: gym?.name || '—',
    joinedDate: formatDate(admin.created_at),
    status,
    subscription: mapSubscription(subscription, plan),
  };
}

function mapAccountRow(account, gym, subscription, plan) {
  const status = account.status === 'suspended' || account.status === 'inactive'
    ? 'suspended'
    : 'active';

  return {
    id: account.id,
    authUserId: account.auth_user_id,
    userAccountId: account.id,
    gymId: account.gym_id,
    name: account.full_name || '—',
    email: account.email || '—',
    phone: '—',
    gym: gym?.name || '—',
    joinedDate: formatDate(account.created_at),
    status,
    subscription: mapSubscription(subscription, plan),
  };
}

async function fetchLatestSubscriptionsByGym(gymIds) {
  if (!gymIds.length) return new Map();

  const { data, error } = await supabase
    .from('gym_subscriptions')
    .select('*')
    .in('gym_id', gymIds)
    .order('created_at', { ascending: false });

  if (error) throw error;

  const map = new Map();
  for (const row of data || []) {
    if (!row.gym_id || map.has(row.gym_id)) continue;
    map.set(row.gym_id, row);
  }
  return map;
}

async function fetchGymsById(gymIds) {
  if (!gymIds.length) return new Map();

  const { data, error } = await supabase
    .from('gyms')
    .select('id, name')
    .in('id', gymIds);

  if (error) throw error;

  return new Map((data || []).map((gym) => [gym.id, gym]));
}

async function fetchPlansById(planIds) {
  const ids = [...new Set(planIds.filter(Boolean))];
  if (!ids.length) return new Map();

  const { data, error } = await supabase
    .from('saas_plans')
    .select('id, name, features')
    .in('id', ids);

  if (error) throw error;

  return new Map((data || []).map((plan) => [plan.id, plan]));
}

export async function fetchGymAdminList() {
  return cachedQuery('owner:gym-admins', async () => {
  const [{ data: adminRows, error: adminError }, { data: accountRows, error: accountError }] = await Promise.all([
    supabase.from('admins').select('*').order('created_at', { ascending: false }),
    supabase.from('user_accounts').select('*').eq('role', 'admin').order('created_at', { ascending: false }),
  ]);

  if (adminError && !/admins.*does not exist|schema cache/i.test(adminError.message)) {
    throw new Error(formatSupabaseError(adminError, 'Could not load gym admins.'));
  }
  if (accountError) {
    throw new Error(formatSupabaseError(accountError, 'Could not load gym admins.'));
  }

  const admins = adminRows || [];
  const accounts = accountRows || [];
  const adminEmails = new Set(admins.map((a) => a.email?.toLowerCase()).filter(Boolean));

  const gymIds = [
    ...new Set([
      ...admins.map((a) => a.gym_id),
      ...accounts.map((a) => a.gym_id),
    ].filter(Boolean)),
  ];

  const gymsById = await fetchGymsById(gymIds);
  const subsByGym = await fetchLatestSubscriptionsByGym(gymIds);
  const planIds = [...subsByGym.values()].map((s) => s.plan_id);
  const plansById = await fetchPlansById(planIds);

  const fromAdmins = admins.map((admin) => {
    const gym = gymsById.get(admin.gym_id);
    const sub = subsByGym.get(admin.gym_id);
    const plan = sub?.plan_id ? plansById.get(sub.plan_id) : null;
    return mapAdminRow(admin, gym, sub, plan);
  });

  const fromAccounts = accounts
    .filter((account) => !adminEmails.has(account.email?.toLowerCase()))
    .map((account) => {
      const gym = gymsById.get(account.gym_id);
      const sub = subsByGym.get(account.gym_id);
      const plan = sub?.plan_id ? plansById.get(sub.plan_id) : null;
      return mapAccountRow(account, gym, sub, plan);
    });

  return [...fromAdmins, ...fromAccounts];
  }, 60_000);
}

export async function fetchGymFilterOptions() {
  return cachedQuery('owner:gym-filter-options', async () => {
  const { data, error } = await supabase
    .from('gyms')
    .select('name')
    .order('name', { ascending: true });

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load gyms.'));
  }

  const names = (data || []).map((g) => g.name).filter(Boolean);
  return ['All Gyms', ...names];
  }, 5 * 60_000);
}

export async function fetchSaasPlanOptions() {
  const { data, error } = await supabase
    .from('saas_plans')
    .select('id, name, monthly_price, quarterly_price, yearly_price, features, status')
    .eq('status', 'active')
    .order('sort_order', { ascending: true });

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load plans.'));
  }

  return (data || []).map((plan) => ({
    id: plan.id,
    name: plan.name,
    monthlyPrice: Number(plan.monthly_price) || 0,
    quarterlyPrice: Number(plan.quarterly_price) || 0,
    yearlyPrice: Number(plan.yearly_price) || 0,
    features: parseFeatures(plan.features),
  }));
}

export async function updateGymAdminEmail(admin, nextEmail) {
  const email = String(nextEmail || '').trim().toLowerCase();
  if (!email || !email.includes('@')) {
    throw new Error('Enter a valid email address.');
  }
  if (!admin?.id) {
    throw new Error('Gym admin not found.');
  }

  const current = String(admin.email || '').trim().toLowerCase();
  if (email === current) {
    return { ...admin, email };
  }

  const { error: rpcError } = await supabase.rpc('owner_set_gym_admin_email', {
    p_admin_id: admin.id,
    p_new_email: email,
  });

  if (rpcError) {
    const msg = String(rpcError.message || '');
    if (/could not find the function|schema cache|does not exist/i.test(msg)) {
      // Fallback when RPC is not deployed yet: update profile tables only
      const { error: adminError } = await supabase
        .from('admins')
        .update({ email, updated_at: new Date().toISOString() })
        .eq('id', admin.id);

      if (adminError && !/admins.*does not exist|schema cache/i.test(adminError.message || '')) {
        if (admin.authUserId) {
          const { error: byAuth } = await supabase
            .from('admins')
            .update({ email, updated_at: new Date().toISOString() })
            .eq('auth_user_id', admin.authUserId);
          if (byAuth && !/admins.*does not exist|schema cache/i.test(byAuth.message || '')) {
            throw new Error(formatSupabaseError(byAuth, 'Could not update admin email.'));
          }
        } else {
          throw new Error(formatSupabaseError(adminError, 'Could not update admin email.'));
        }
      }

      const accountId = admin.userAccountId || admin.id;
      let { error: accountError } = await supabase
        .from('user_accounts')
        .update({ email })
        .eq('id', accountId);

      if (accountError && admin.authUserId) {
        ({ error: accountError } = await supabase
          .from('user_accounts')
          .update({ email })
          .eq('auth_user_id', admin.authUserId)
          .eq('role', 'admin'));
      }
      if (accountError) {
        throw new Error(formatSupabaseError(accountError, 'Could not update account email.'));
      }
    } else {
      throw new Error(formatSupabaseError(rpcError, 'Could not update email.'));
    }
  }

  invalidateCache('owner:');
  return { ...admin, email };
}

/**
 * When owner changes a gym subscription "Contact Email", keep gyms + gym admin
 * Users dashboard email in sync (login email via owner_set_gym_admin_email RPC).
 */
export async function syncGymContactEmailToAdmin(gymId, nextEmail) {
  const email = String(nextEmail || '').trim().toLowerCase();
  if (!gymId || !email || !email.includes('@')) return null;

  await supabase
    .from('gyms')
    .update({ contact_email: email })
    .eq('id', gymId);

  let adminRow = null;
  const { data: byGym } = await supabase
    .from('admins')
    .select('id, auth_user_id, email, gym_id, full_name, phone, status, created_at')
    .eq('gym_id', gymId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  adminRow = byGym;

  let accountRow = null;
  const { data: account } = await supabase
    .from('user_accounts')
    .select('id, auth_user_id, email, gym_id, full_name, status, created_at')
    .eq('gym_id', gymId)
    .eq('role', 'admin')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  accountRow = account;

  if (!adminRow && !accountRow) {
    invalidateCache('owner:');
    return null;
  }

  const mapped = adminRow
    ? {
      id: adminRow.id,
      authUserId: adminRow.auth_user_id,
      userAccountId: accountRow?.id || null,
      gymId: adminRow.gym_id || gymId,
      email: adminRow.email || accountRow?.email || '',
      name: adminRow.full_name || accountRow?.full_name || '—',
    }
    : {
      id: accountRow.id,
      authUserId: accountRow.auth_user_id,
      userAccountId: accountRow.id,
      gymId: accountRow.gym_id || gymId,
      email: accountRow.email || '',
      name: accountRow.full_name || '—',
    };

  return updateGymAdminEmail(mapped, email);
}

export async function updateGymAdminStatus(admin, nextStatus) {
  const dbStatus = nextStatus === 'suspended' ? 'suspended' : 'active';

  if (admin.authUserId) {
    const { data: adminRow } = await supabase
      .from('admins')
      .select('id')
      .eq('auth_user_id', admin.authUserId)
      .maybeSingle();

    if (adminRow?.id) {
      const { error: adminError } = await supabase
        .from('admins')
        .update({ status: dbStatus, updated_at: new Date().toISOString() })
        .eq('id', adminRow.id);

      if (adminError && !/admins.*does not exist|schema cache/i.test(adminError.message)) {
        throw new Error(formatSupabaseError(adminError, 'Could not update admin status.'));
      }
    }
  } else {
    const { error: adminError } = await supabase
      .from('admins')
      .update({ status: dbStatus, updated_at: new Date().toISOString() })
      .eq('id', admin.id);

    if (adminError && !/admins.*does not exist|schema cache/i.test(adminError.message)) {
      throw new Error(formatSupabaseError(adminError, 'Could not update admin status.'));
    }
  }

  const accountId = admin.userAccountId || admin.id;
  const { error: accountError } = await supabase
    .from('user_accounts')
    .update({ status: dbStatus })
    .eq('id', accountId);

  if (accountError) {
    if (admin.authUserId) {
      const { error: byAuthError } = await supabase
        .from('user_accounts')
        .update({ status: dbStatus })
        .eq('auth_user_id', admin.authUserId);

      if (byAuthError) {
        throw new Error(formatSupabaseError(byAuthError, 'Could not update login account.'));
      }
    } else {
      throw new Error(formatSupabaseError(accountError, 'Could not update login account.'));
    }
  }

  invalidateCache('owner:');
  return { ...admin, status: nextStatus };
}
